import { forwardRef } from 'react';
import { FileViewer } from '@/components/common/media-content/file-viewer';
import VideoViewer from '@/components/common/media-content/video-viewer';
import { SafeImage } from '@/components/common/safe-image';
import { ConversationMessageContentType } from '@/models/conversations/conversation';
import cn from '@/utils/merge-class-names';
import ImageZoomPan, { type ImageZoomActions } from './image-zoom-pan';

interface Props {
  type: ConversationMessageContentType;
  src: string;
  className?: string;
  isInView?: boolean;
  autoPlay?: boolean;
  onImageZoom?: (isZooming: boolean) => void;
}

const MediaContent = forwardRef<ImageZoomActions, Props>((props, ref) => {
  const { type, src, className, isInView = true, autoPlay = false, onImageZoom } = props;
  return (
    <div
      className={cn('relative flex flex-1 size-full items-center justify-center', {
        'items-stretch justify-start': type === ConversationMessageContentType.Text,
      })}>
      <span className='loading text-primary loading-spinner absolute left-1/2 top-1/2 -translate-x-1/2 loading-lg -translate-y-1/2 -z-10' />
      {type === ConversationMessageContentType.Image ? (
        <ImageZoomPan
          ref={ref}
          onImageZoom={onImageZoom}
          src={src}
          isInView={isInView}
          className={className}>
          <SafeImage
            displayType='img'
            src={src}
            isInView={isInView}
            className={cn(
              'aspect-auto h-auto max-h-[calc(100dvh-200px)] desktop:max-w-[60rem] w-full desktop:h-[33rem] desktop:object-contain desktop:bg-black',
              className,
            )}
            alt='media content'
          />
        </ImageZoomPan>
      ) : type === ConversationMessageContentType.Video ? (
        <VideoViewer
          controls
          autoPlay={autoPlay}
          src={src}
          isInView={isInView}
          preload='metadata'
          className={cn(
            'max-h-[calc(100dvh-200px)] w-full object-contain desktop:max-w-[58rem] desktop:h-[37.5rem]',
            className,
          )}
        />
      ) : type === ConversationMessageContentType.File ? (
        <FileViewer
          className={className}
          src={src}
        />
      ) : (
        <div
          className={cn(
            'break-all text-sm max-h-[calc(100dvh-200px)] overflow-y-auto overflow-x-hidden whitespace-pre-wrap',
            className,
          )}>
          {src}
        </div>
      )}
    </div>
  );
});
export default MediaContent;
